import { ref, watch, toValue, type MaybeRefOrGetter } from 'vue'
import type { Product } from '@scayle/storefront-nuxt'
import {
  DEFAULT_PRICE_PROMOTION_KEY,
  DEFAULT_PRODUCT_WITH,
} from '#layers/base/constants/withParameters'
import { useRpc } from '#storefront/composables'
import { getLowestPrice } from '~/utils/product'

const SHOP_CODE = 'go'

/** API takes product ID (item_nr), returns list of other product IDs; we fetch those by ID. */
export function useShopTheLookProducts(
  productId: MaybeRefOrGetter<number | undefined>,
) {
  const shopTheLookProducts = ref<Product[]>([])
  const isLoading = ref(false)

  async function fetchShopTheLookProducts() {
    const id = toValue(productId)
    if (id == null) {
      shopTheLookProducts.value = []
      return
    }

    isLoading.value = true
    try {
      const mappingsCacheKey = `outfit_mappings_${id}`
      const mappingsRpc = await useRpc('getOutfitMappings', mappingsCacheKey, {
        productId: id,
        shopCode: SHOP_CODE,
      })

      const rawData = mappingsRpc.data?.value ?? []
      const mappings = rawData.filter(
        (s) => Number.isFinite(s.productId) && s.productId !== id,
      )

      const positionIndexById = Object.fromEntries(
        mappings.map((s) => [s.productId, s.positionIndex]),
      ) as Record<number, number>

      const productIds = mappings.map((s) => s.productId)

      if (!productIds.length) {
        shopTheLookProducts.value = []
        return
      }

      const cacheKey = `shop_the_look_${productIds.join('-')}`
      const rpcData = await useRpc('getProductsByIdsParallel', cacheKey, {
        productIds,
        with: DEFAULT_PRODUCT_WITH,
        pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
      })

      const fetched = (rpcData.data?.value || []) as Product[]

      shopTheLookProducts.value = fetched
        .filter((p) => {
          // Keep only items that can actually render a price in the UI.
          return (
            Number.isFinite(positionIndexById[p.id]) &&
            getLowestPrice(p.variants || []) !== undefined
          )
        })
        .slice()
        .sort((a, b) => {
          const positionA = positionIndexById[a.id]
          const positionB = positionIndexById[b.id]
          return positionA === undefined || positionB === undefined
            ? 0
            : positionA - positionB
        })
    } catch {
      shopTheLookProducts.value = []
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => toValue(productId),
    (id) => {
      if (id != null) {
        fetchShopTheLookProducts()
      } else {
        shopTheLookProducts.value = []
      }
    },
    { immediate: true },
  )

  return {
    shopTheLookProducts,
    isLoading,
  }
}
